import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionServerSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { MapDispatchToProps } from '~src/commonInfra/utils/ConnectUtils';
import { ConnectedApplicationSubspaceRouter } from '~src/connectedApplications/components/ConnectedApplicationSubspaceRouter';
import { HomeUserUtils } from '~src/home/utils/HomeUserUtils';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import {
  IntegrationsSettingsSubspaceComponent,
  IntegrationsSubspaceName
} from '~src/settings/IntegrationsSettings/IntegrationsSettingsSubspace';
import { MobileSettingsSubspaceName } from '~src/settings/mobile-settings/MobileSecuritySettingsShared';
import { fetchPrivateConnectionsRequest } from '~src/settings/privateconnect-settings/actions/FetchPrivateConnectionsRequestAction';
import { SecuritySiteSettingsSubspaceComponent } from '~src/settings/security-settings/SecuritySiteSettingsSubspace';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { AuthenticationSubspaceRouter } from '~src/settings/siteAuthSettings/components/AuthenticationSubspaceRouter';
import { Sites } from '~src/siteManagement/services/resources/Sites';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { SiteSettingsSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import { SiteRoles } from '~src/userManagement/users/serverApi/types/ISiteRole';

import { AppState } from '../../../../bootstrap/redux/state/AppState';
import { PrivateConnectSiteSettingsSubspace } from '../../../privateconnect-settings/PrivateConnectSiteSettingsSubspace';
import { AuthenticationSiteSettingsSubspace } from './AuthenticationSettingsSubspace';
import { BridgeSiteSettingsSubspace } from './BridgeSiteSettingsSubspace';
import { DataConnectSiteSettingsSubspace } from './DataConnectSiteSettingsSubspace';
import { ExtensionsSiteSettingsSubspaceComponent } from './extensions/ExtensionsSiteSettingsSubspace';
import { GeneralSiteSettingsSubspace } from './GeneralSiteSettingsSubspace';
import { MobileSiteSettingsSubspaceComponent } from './MobileSiteSettingsSpace';
import { PackagesSiteSettingsSubspace } from './packages/PackagesSiteSettingsSubspace';

export interface SiteSettingsSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly siteName: string;
  readonly subspaceName: SiteSettingsSubspaceName;
}

export interface SiteSettingsServicesProps {
  readonly server: SessionManagementService;
}

export interface SiteSettingsStateProps {
  readonly doesTheServerHaveMoreThanOneSite: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly userRole: string;
  readonly isOnlineEnvironment: boolean;
  readonly stateSubspaceName: SiteSettingsSubspaceName;
  readonly privateConnectEnabled: boolean;
  readonly canManagePackages: boolean;
}

export interface SiteSettingsServicesDispatchProps {
  fetchPrivateConnections: (server: SessionManagementService) => void;
}

export type SiteSettingsNonServicesProps = SiteSettingsSpaceOwnProps & SiteSettingsStateProps;
export type SiteSettingsSpaceProps = SiteSettingsNonServicesProps & SiteSettingsServicesProps & SiteSettingsServicesDispatchProps;

export class SiteSettingsSpaceComponent extends React.PureComponent<SiteSettingsSpaceProps> {

  public componentDidMount(): void {
    if(this.props.isOnlineEnvironment){
      this.props.fetchPrivateConnections(this.props.server);
    }
  }

  public render() {
    const subspaces = getSettingsSubspaces(
      this.props.urls,
      this.props.siteName,
      this.props.server,
      this.props.isSessionServerAdmin,
      this.props.userRole,
      this.props.isOnlineEnvironment,
      this.props.subspaceName,
      this.props.stateSubspaceName,
      this.props.privateConnectEnabled,
      this.props.canManagePackages
    );

    // A profile holder landing on /settings (General) won't have that tab after filtering; fall
    // back to the first tab they can see so the content area isn't blank. No-op for admins, whose
    // requested tab is always present.
    const activeSubspaceName = subspaces.some(s => s.name === this.props.subspaceName)
      ? this.props.subspaceName
      : (subspaces.length > 0 ? subspaces[0].name as SiteSettingsSubspaceName : this.props.subspaceName);

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={activeSubspaceName}
        urlForSubspace={this.getSettingsUrl}
        mastheadElement={null}
        testIdPrefix={'site-settings'}
        spaceItemName={this.props.doesTheServerHaveMoreThanOneSite ? Messages.siteSettings() : Messages.settings()}
      />
    );
  }

  private static settingsUrlBySubspace: Record<SiteSettingsSubspaceName, (urls: SpacesSiteUrls) => string> = {
    settings: (urls) => urls.settingsUrl(),
    extensions: (urls) => urls.settingsExtensionsUrl(),
    packages: (urls) => urls.settingsPackagesUrl(),
    bridge: (urls) => urls.settingsBridgeUrl(),
    dataconnect: (urls) => urls.settingsDataConnectUrl(),
    authentication: (urls) => urls.settingsAuthenticationUrl(),
    integrations: (urls) => urls.settingsIntegrationsUrl(),
    connectedApplications: (urls) => urls.settingsConnectedApplicationsUrl(),
    mobile: (urls) => urls.settingsMobileUrl(),
    privateconnect: (urls) => urls.settingsPrivateConnectUrl(),
    security: (urls) => urls.settingsSecurityUrl(),
  };

  private getSettingsUrl = (subspaceName?: SiteSettingsSubspaceName) => {
    if (subspaceName) {
      return SiteSettingsSpaceComponent.settingsUrlBySubspace[subspaceName](this.props.urls);
    }
    return '';
  };
}

export function getSettingsSubspaces(
  urls: SpacesSiteUrls,
  siteName: string,
  server: SessionManagementService,
  isSessionServerAdmin: boolean,
  userRole: string,
  isOnlineEnvironment: boolean,
  subspaceName: SiteSettingsSubspaceName,
  stateSubspaceName: SiteSettingsSubspaceName,
  privateConnectEnabled:  boolean,
  canManagePackages?: boolean
): ReadonlyArray<Subspace> {
  const subspaces = _.compact<_.List<Subspace | false>>([
    {
      name: 'settings',
      tabLabel: Messages.general(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <GeneralSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
          serverSettingsService={ServerSettingsInstance}
        />
      )
    },
    ServerSettingsUnauthenticated.isSiteSamlEnabled() && {
      name: 'authentication',
      tabLabel: Messages.authentication(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        FeatureFlags.featureFlagForMultipleIDPSupportEnabled ?
          <AuthenticationSubspaceRouter
            siteName={siteName}
            urls={urls}
            server={server}
            isSessionServerAdmin={isSessionServerAdmin}
            serverSettingsService={ServerSettingsInstance}
            sites={Sites.instance}/> :
          <AuthenticationSiteSettingsSubspace
            siteName={siteName}
            urls={urls}
            server={server}
            isSessionServerAdmin={isSessionServerAdmin}
            serverSettingsService={ServerSettingsInstance}
            sites={Sites.instance}
          />
      )
    },
    ServerSettingsUnauthenticated.isBridgeEnabled() && {
      name: 'bridge',
      tabLabel: Messages.bridge(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <BridgeSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    ServerSettingsUnauthenticated.isBridgeEnabled() && FeatureFlags.enableDataConnect && {
      name: 'dataconnect',
      tabLabel: Messages.dataConnect(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <DataConnectSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    FeatureFlags.extensionsSettingsTabEnabled && isSiteOrServerAdmin(server, isSessionServerAdmin) && {
      name: 'extensions',
      tabLabel: Messages.extensionsSettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <ExtensionsSiteSettingsSubspaceComponent
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    canSeePackagesTab(server, isSessionServerAdmin, !!canManagePackages) && {
      name: 'packages',
      tabLabel: Messages.packages_tab_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <PackagesSiteSettingsSubspace />
      )
    },
    isOnlineEnvironment && privateConnectEnabled && {
      name: 'privateconnect',
      tabLabel: Messages.privateconnect(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <PrivateConnectSiteSettingsSubspace
          isSessionServerAdmin={isSessionServerAdmin}
          isOnlineEnvironment={isOnlineEnvironment}
        />
      )
    },
    isUserAuthorizedForIntegrationsTab(userRole) && {
      name: IntegrationsSubspaceName,
      tabLabel: Messages.integrations_settingsTabTitle(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <IntegrationsSettingsSubspaceComponent
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
          isOnlineEnvironment={isOnlineEnvironment}
          userRole={userRole}
          sites={Sites.instance}
          isSingleSiteServerWithIntegrations={false}
          isSiteSettings={true}
        />
      )
    },
    isSiteOrServerAdmin(server, isSessionServerAdmin) && {
      name: 'connectedApplications',
      tabLabel: Messages.connectedApplications(),
      //TODO: Add contentCount -- contentCount: 1,
      shouldRenderTabContentCount: false,
      renderContent: () => {
        if (subspaceName === stateSubspaceName) {
          return <ConnectedApplicationSubspaceRouter />;
        }
      }
    },
    isSiteOrServerAdmin(server, isSessionServerAdmin) && {
      name: MobileSettingsSubspaceName,
      tabLabel: Messages.mobileSettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <MobileSiteSettingsSubspaceComponent
          server={server}
        />
      )
    },
    isOnlineEnvironment && FeatureFlags.securitySettingsEnabled && {
      name: 'security',
      tabLabel: Messages.securitySettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <SecuritySiteSettingsSubspaceComponent
          server={server}
          sites={Sites.instance}
        />
      )
    },
  ]);

  // Admins see the full set above. An Admin Profile holder (non-admin) reaches the settings
  // space via the 'Setting' resource but should only see the tabs their profile grants -- today
  // that is Packages (via the Package resource). Restrict to the resource-backed tabs so a
  // profile holder never lands on General/Extensions/etc. they cannot use.
  if (!isSiteOrServerAdmin(server, isSessionServerAdmin)) {
    return subspaces.filter(subspace => ADMIN_PROFILE_SETTINGS_TABS.has(subspace.name));
  }

  return subspaces;
}

// Settings tabs an Admin Profile holder may see.
const ADMIN_PROFILE_SETTINGS_TABS: Set<string> = new Set<string>(['packages']);

export function isSiteOrServerAdmin(server: SessionManagementService, isSessionServerAdmin: boolean): boolean {
  return isSessionServerAdmin || server.isSessionSiteAdmin();
}

// Whether the Packages settings tab should be shown. Admins always see it; a non-admin sees it only
// as an Admin Profile holder who can act on Packages.
export function canSeePackagesTab(
  server: SessionManagementService,
  isSessionServerAdmin: boolean,
  canManagePackages: boolean
): boolean {
  if (!FeatureFlags.packagesEnabled) {
    return false;
  }
  // Non-admins reach this tab only via Admin Profiles: adminProfilesForSiteSettingsEnabled (the
  // settings-space master switch) lets them into Settings, and adminProfilesForPackagesEnabled (the
  // client mirror of the server AdminProfilesForPackages FF) gates the Packages tab specifically so
  // it can roll out independently of settings-space access.
  return isSiteOrServerAdmin(server, isSessionServerAdmin)
    || (FeatureFlags.adminProfilesForSiteSettingsEnabled
        && FeatureFlags.adminProfilesForPackagesEnabled
        && canManagePackages);
}

export function isUserAuthorizedForIntegrationsTab(userRole: String) {
  const isAdminCreator = (userRole === SiteRoles.ServerAdministrator) || (userRole === SiteRoles.SiteAdministratorCreator);
  const isAdminExplorer = userRole === SiteRoles.SiteAdministratorExplorer;

  return isAdminCreator || isAdminExplorer;
}

const mapDispatchToProps: MapDispatchToProps<SiteSettingsServicesDispatchProps> =
  (dispatch) => bindActionCreators({
    fetchPrivateConnections: fetchPrivateConnectionsRequest
  },
  dispatch);

export const ConnectedSiteSettingsSpace: React.ComponentClass<SiteSettingsSpaceOwnProps & SiteSettingsServicesProps>
  = connect((state: AppState): SiteSettingsStateProps => ({
    doesTheServerHaveMoreThanOneSite: SessionServerSelectors.doesTheServerHaveMoreThanOneSite(state),
    isSessionServerAdmin: SessionUserSelectors.isSessionServerAdmin(state),
    userRole: HomeUserUtils.getRole(state),
    isOnlineEnvironment: SessionServerSelectors.isOnlineEnvironment(state),
    stateSubspaceName: selectSpaceInfo(state).subspaceName as SiteSettingsSubspaceName,
    privateConnectEnabled: state.privateConnectInfo.privateConnections.length > 0,
    canManagePackages: getSessionManagementServiceInstance().canSessionManageUsersOrActOnResource('Package', ['SEE_SITE_PACKAGES'])
  }), mapDispatchToProps)(SiteSettingsSpaceComponent);

